import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AnimeCard from '@/components/feature/AnimeCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { animeList, animeCategories } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'latest'>('latest');

  const currentCategory = animeCategories.find((c) => c.slug === slug);
  const filteredAnime = useMemo(() => {
    let list = animeList.filter((a) => a.categorySlug === slug);
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else {
      list = [...list].sort((a, b) => b.updateDate.localeCompare(a.updateDate));
    }
    return list;
  }, [slug, sortBy]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-800 mb-2">分类不存在</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 羞羞动漫网站`,
    description: `羞羞动漫网站${currentCategory.name}专区，为您精选${currentCategory.count}部${currentCategory.name}相关动漫作品，免费在线观看。`,
    url: `https://www.xiuxiudongman.com/category/${slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="mt-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${currentCategory.icon} text-lg text-primary-500`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500">
                    共 {currentCategory.count} 部作品 · 51动漫国产精品入口
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 mr-1 whitespace-nowrap">排序：</span>
                {[
                  { key: 'latest' as const, label: '最新更新', icon: 'ri-time-line' },
                  { key: 'rating' as const, label: '评分最高', icon: 'ri-star-line' },
                  { key: 'views' as const, label: '最多观看', icon: 'ri-fire-line' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1 transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-white'
                        : 'bg-white border border-background-200/70 text-foreground-600 hover:border-primary-300'
                    }`}
                  >
                    <i className={opt.icon}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredAnime.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-product-shop="">
                {filteredAnime.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">暂无该分类的动漫作品</p>
              </div>
            )}

            <div className="mt-8 bg-white rounded-lg border border-background-200/70 p-4 md:p-5">
              <h3 className="text-sm font-medium text-foreground-900 mb-2">其他分类</h3>
              <div className="flex flex-wrap gap-2">
                {animeCategories
                  .filter((c) => c.slug !== slug)
                  .map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="px-3 py-1.5 bg-background-50 border border-background-200/70 rounded-full text-xs text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}